'use client'
import { useState, useCallback, useEffect, SetStateAction } from 'react';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { IDraftEmailSave, IDraftInputs } from '@/lib/interfaces';
import { useSearchParams } from 'next/navigation';
import { ToastError } from '@/lib/sonnerUtils';
import FilterEx from '@/lib/badWordsUtils';
import { useRouter } from "next/navigation";
import { generateEmailDraft, SaveNewDraft } from './actions';
import DraftDetailsSection from './draftDetailsSection';
import DraftGeneratedSection from './draftGeneratedSection';
import Link from 'next/link';
import LoginRedirect from '@/components/LoginRedirect';
import AdSterraNativeBanner from '@/components/adsterra-ui/adsterra-native-banner';
import AdSterraIFrameBannerAuto from '@/components/adsterra-ui/adsterra-ifram-auto';

function NewEmailDraftWrapped() {
    const searchParams = useSearchParams();
    const [ userId, setUserId ] = useState<string>(searchParams.get('userId')?.toString() ?? '');  
    const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
    const [draft, setDraft] = useState<IDraftInputs>({
      userRole: '',
      title: '',
      recipientRole: '',
      tone: '',
      details: '',
    });
    const [generatedEmail, setGeneratedEmail] = useState('');
    const [isMyLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const router = useRouter();  

    // Function to handle the AI generation API call
    const handleGenerate = useCallback(async () => {
      // 1. Basic Validation
      if (!draft.userRole || !draft.recipientRole || !draft.tone || !draft.details) {
        setError('Please fill in all required fields (Roles, Tone, and Details).');
        return;
      }

      // Reset states
      setIsLoading(true);
      setError(null);
      setGeneratedEmail('');
      setCopySuccess(false);

      try {
        const payload = { draft, userId };
        const actionResponse = await generateEmailDraft(payload);
        
        // force user tokens balance component to refresh
		    window.dispatchEvent(new Event('tokens-deducted'));
        
        if (actionResponse.status !== 200) {
          // Handle server-side errors (e.g., missing API key, model failure)
          throw new Error(actionResponse.error || 'Failed to generate email draft due to a server error.');
        }

        // 3. Update the state with the successful result
        setGeneratedEmail(actionResponse.email!);
      } catch (err) {
        console.error('Generation Error:', err);
        setError('An error occurred. Please check the console for details and ensure your GEMINI_API_KEY is set.');
      } finally {
        setIsLoading(false);
      }
    }, [draft]);

    // Function to copy the generated text to the clipboard
    const handleCopy = useCallback(() => {
      if (generatedEmail) {
        navigator.clipboard.writeText(generatedEmail).then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000); // Reset success message after 2s
        });
      }
    }, [generatedEmail]);

    const handleSaveDraft = async () => {
      // gather the required data to send to the API
      const desData: IDraftEmailSave = {
        userId: userId!,
        userRole: draft.userRole,
        recipientRole: draft.recipientRole,
        tone: draft.tone,
        details: draft.details,
        title: draft.title,
        generatedEmail: generatedEmail
      }
      
      const response = await SaveNewDraft(desData);

      // handle the response from the fetch()
      console.log(response);

    }

    const handleIsProfane = (text: string): string => {
      const filter = new FilterEx();

      if(filter.isProfane(text)) {
        const filteredText = filter.cleanExclude(text);
        setError("The usage of profane, offensive and inappropriate words is not allowed!");
        ToastError("The usage of profane, offensive and inappropriate words is not allowed!");
        return filteredText;
      } else {
        setError(null);
        return text;
      }
    }
    // max-w-7xl mx-auto
    return (
      <>
        <div className="space-y-8">
          <div className='text-lg'>
            <Link className='underline' href={"/"}>Home</Link>
            <span className='pl-2 pr-2'>{">"}</span>
            <Link className='underline' href={"/dashboard"}>Dashbord</Link>
            <span className='pl-2 pr-2'>{"> Email Draft Generator"}</span>
          </div>

          <AdSterraNativeBanner />

          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Email Drafts Generator
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Input your context below to let the AI draft a perfect email for you.
          </p>

          {/* Error Message Display */}
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md shadow-sm" role="alert">
              <p className="font-bold">Generation Failed</p>
              <p>{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* -------------------- Input Form Card -------------------- */}
            <DraftDetailsSection 
              isMyLoading={isMyLoading} draft={draft} 
              handleIsProfane={handleIsProfane} 
              handleGenerate={handleGenerate} 
              setDraft={setDraft} />

            {/* -------------------- Output Result Card -------------------- */}
            <DraftGeneratedSection 
              isMyLoading={isMyLoading} copySuccess={copySuccess} userId={userId} generatedEmail={generatedEmail} 
              handleCopy={handleCopy} handleSaveDraft={handleSaveDraft} />

          </div>
        </div>

        <AdSterraIFrameBannerAuto position='bottom' />

        {/* 2. THE OVERLAY (Only shows if not logged in) */}
        {(!isAuthenticated && !isLoading) && (
          <LoginRedirect message="to analyze this job description" />
        )}
      </>
    );
  }

  export default NewEmailDraftWrapped